import type { SerialEntry } from "./types";

/**
 * Pure mapping for the serial reference table (thunks = workflow, logic.ts
 * = calculation — section 43). The Cube response returns one row per serial
 * pair with the raw `Dbo520serial.*` column names.
 */

/** Cube dimensions of the serial reference query (Dbo520serial table). */
export const SERIAL_LIST_DIMENSIONS = [
  "Dbo520serial.MPKey",
  "Dbo520serial.SERIAL",
  "Dbo520serial.SERIAL_1",
  "Dbo520serial.ID",
  "Dbo520serial.IS_SES_LINKED_DED",
] as const;

type SerialRow = Record<string, unknown>;

function cell(row: SerialRow, key: string): string {
  return row[key] === undefined || row[key] === null
    ? ""
    : String(row[key]);
}

// CubeJS serializes booleans natively, but the raw-row type is
// string|number — read through `unknown` so a "true"/"false" string
// transport (or a real boolean) both normalize correctly.
function boolCell(row: SerialRow, key: string): boolean {
  const value: unknown = row[key];
  return value === true || value === "true";
}

/** Maps one Cube row into a `SerialEntry` (pure, unit-testable). */
export function mapSerialRow(row: SerialRow): SerialEntry {
  return {
    mpKey: cell(row, "Dbo520serial.MPKey"),
    serial: cell(row, "Dbo520serial.SERIAL"),
    serial1: cell(row, "Dbo520serial.SERIAL_1"),
    id: cell(row, "Dbo520serial.ID"),
    isSesLinked: boolCell(row, "Dbo520serial.IS_SES_LINKED_DED"),
  };
}

/** Maps a full Cube response into serial entries. */
export function mapSerialRows(rows: SerialRow[]): SerialEntry[] {
  return rows.map(mapSerialRow);
}

/** Dropdown option shape for the upcoming serial filter. */
export interface SerialFilterOption {
  /** Filter value — the stable `MPKey`. */
  value: string;
  /** User-facing label — the display `SERIAL`. */
  label: string;
}

/** Builds filter options (mpKey value, serial label) from the entries. */
export function buildSerialFilterOptions(entries: SerialEntry[]): SerialFilterOption[] {
  return entries.map((entry) => ({ value: entry.mpKey, label: entry.serial }));
}
